import * as React from "react";
import { StatusIndicator } from "../display/StatusIndicator";

import { FormFieldContext, useFormFieldProps } from "../salt/form-field-context";
import { Label, Text, type TextProps } from "../display/Text";
import "./form-field.css";

/**
 * FormField and its two parts, replacing `@salt-ds/core`'s for 144 FormField elements.
 *
 * ── THIS COMPONENT'S JOB IS WIRING, NOT PAINT ─────────────────────────────────────────
 *
 * FormField mints one id and derives two from it — `label-{id}` and `helperText-{id}` — then
 * publishes them, plus `disabled`, `readOnly`, `necessity` and `validationStatus`, through a
 * context every input reads. That is what connects a visible label and a helper message to the
 * control they describe, for a screen reader that cannot see the layout.
 *
 * It provides the VENDOR'S context object on purpose. See `../salt/form-field-context` — the
 * short version is that a context is keyed by object identity, so a provider of our own would
 * be invisible to the twenty Salt inputs still reading Salt's, and would drop their aria
 * wiring silently rather than loudly.
 *
 * ── THE LABEL CLICK HANDLER IS NOT DECORATION ─────────────────────────────────────────
 *
 * `FormFieldLabel` is not a native `<label htmlFor>`: it has no single target, because the
 * control it describes might be a Dropdown, a group of radios, or a combobox with its own
 * inner input. So it finds the element that claims this label — `[aria-labelledby*="{id}"]`,
 * scoped to this field via `formFieldRef` — and focuses and clicks it. Drop that and clicking
 * a label stops focusing its input, which is a real loss for anyone with a large pointer
 * target need and passes every render test.
 */
export type LabelPlacement = "top" | "left" | "right";
export type Necessity = "required" | "asterisk" | "optional";
export type ValidationStatus = "error" | "warning" | "success";

export interface FormFieldProps extends React.HTMLAttributes<HTMLDivElement> {
  disabled?: boolean;
  labelPlacement?: LabelPlacement;
  necessity?: Necessity;
  readOnly?: boolean;
  validationStatus?: ValidationStatus;
}

function capitalize(value: string): string {
  return value ? value[0].toUpperCase() + value.slice(1) : value;
}

export const FormField = React.forwardRef<HTMLDivElement, FormFieldProps>(function FormField(
  {
    children,
    className,
    disabled = false,
    id: idProp,
    labelPlacement = "top",
    necessity,
    readOnly = false,
    validationStatus,
    ...rest
  },
  ref,
) {
  const formFieldRef = React.useRef<HTMLDivElement | null>(null);
  const generatedId = React.useId();
  const formId = idProp ?? generatedId;
  const labelId = formId ? `label-${formId}` : undefined;
  const helperTextId = formId ? `helperText-${formId}` : undefined;

  const setRefs = React.useCallback(
    (node: HTMLDivElement | null) => {
      formFieldRef.current = node;
      if (typeof ref === "function") ref(node);
      else if (ref) (ref as React.MutableRefObject<HTMLDivElement | null>).current = node;
    },
    [ref],
  );

  const classes = ["saltFormField"];
  if (disabled) classes.push("saltFormField-disabled");
  classes.push(`saltFormField-label${capitalize(labelPlacement)}`);
  if (className) classes.push(className);

  // Memoised because this object is a context value: a fresh object every render re-renders
  // every input in the field on each keystroke of any one of them.
  const value = React.useMemo(
    () => ({
      a11yProps: { "aria-labelledby": labelId, "aria-describedby": helperTextId },
      disabled,
      necessity,
      readOnly,
      validationStatus,
      formFieldRef,
    }),
    [disabled, helperTextId, labelId, necessity, readOnly, validationStatus],
  );

  return (
    <div className={classes.join(" ")} ref={setRefs} {...rest}>
      <FormFieldContext.Provider value={value}>{children}</FormFieldContext.Provider>
    </div>
  );
});

/*
 * Derived from `TextProps<"label">`, exactly as the vendor declares it, because call sites
 * pass `htmlFor` — this renders a real <label>, and `htmlFor` exists on no other element.
 * `variant` and `styleAs` are omitted because this component fixes both.
 *
 * `intent` is "label" | "sentence". It was first written here as "label" | "secondary", which
 * typechecked and would have emitted `saltFormFieldLabel-secondary` — a class no stylesheet
 * defines, so the more prominent label style would simply never appear.
 */
export interface FormFieldLabelProps
  extends Omit<TextProps<"label">, "variant" | "styleAs" | "color"> {
  intent?: "label" | "sentence";
}

export function FormFieldLabel({
  children,
  className,
  intent = "label",
  onClick,
  ...rest
}: FormFieldLabelProps) {
  const { a11yProps, disabled, necessity, formFieldRef } = useFormFieldProps();
  const id = a11yProps?.["aria-labelledby"];

  const handleClick = (event: React.MouseEvent<HTMLLabelElement>) => {
    if (formFieldRef?.current && id) {
      const element = formFieldRef.current.querySelector<HTMLElement>(
        `[aria-labelledby*="${id}"]`,
      );
      element?.focus();
      element?.click();
    }
    onClick?.(event);
  };

  const necessityLabel = necessity
    ? necessity === "asterisk"
      ? " *"
      : ` (${capitalize(necessity)})`
    : undefined;

  const classes = ["saltFormFieldLabel", `saltFormFieldLabel-${intent}`];
  if (className) classes.push(className);

  return (
    <Label
      className={classes.join(" ")}
      color="secondary"
      disabled={disabled}
      id={id}
      onClick={handleClick}
      {...rest}
    >
      {children}
      {necessityLabel ? (
        <span className="saltFormFieldLabel-necessityLabel">{necessityLabel}</span>
      ) : null}
    </Label>
  );
}

export type FormFieldHelperTextProps = Omit<React.HTMLAttributes<HTMLElement>, "color">;

export function FormFieldHelperText({ children, className, ...rest }: FormFieldHelperTextProps) {
  const { a11yProps, disabled, readOnly, validationStatus } = useFormFieldProps();

  const classes = ["saltFormFieldHelperText"];
  if (validationStatus) classes.push("saltFormFieldHelperText-withValidation");
  if (className) classes.push(className);

  return (
    <div className={classes.join(" ")}>
      {/*
        The status icon is suppressed when the field is disabled or read-only: an error marker
        beside a control the user cannot act on is noise, not information. StatusIndicator is
        still the vendor's because it resolves through Salt's icon registry, which we keep.
      */}
      {!disabled && !readOnly && validationStatus ? (
        <StatusIndicator className="saltFormFieldHelperText-statusIndicator" status={validationStatus} />
      ) : null}
      <Text
        color={validationStatus ?? "secondary"}
        disabled={disabled}
        id={a11yProps?.["aria-describedby"]}
        styleAs="label"
        {...rest}
      >
        {children}
      </Text>
    </div>
  );
}
